import { useEffect, useRef, useState } from "react";
import { loadGoogleMapsPlaces } from "../../../utils/loadGoogleMaps";
import { TextField } from "@mui/material";
import useEnhancedEffect from "@mui/utils/useEnhancedEffect";

const KEY_ESCAPE = "Escape";

function GoogleAutocompleteEditCell(props) {
  const { id, field, value, hasFocus, autocompleteOptions, api } = props;
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value);
  const [, setStatus] = useState("idle");
  const [, setError] = useState("");

  const handleChange = (event) => {
    const newValue = event.target.value;
    setInternalValue(newValue);
    api.setEditCellValue({ id, field, value: newValue });
  };

  // Focus management per MUI edit cell conventions
  useEnhancedEffect(() => {
    if (hasFocus && inputRef.current) {
      const el = inputRef.current;
      try {
        el.focus();
        // Select all for quick overwrite
        if (typeof el.select === "function") el.select();
      } catch (_) {}
    }
  }, [hasFocus]);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        setStatus("loading");
        setError("");
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          setStatus("error");
          setError("Missing Google Maps API key");
          return;
        }
        await loadGoogleMapsPlaces(apiKey);
        if (!mounted || !inputRef.current || !window.google?.maps?.places) {
          setStatus("error");
          setError("Google Places not available");
          return;
        }
        const mergedOptions = {
          // Keep options minimal to avoid API quirks; allow overrides from callers
          componentRestrictions: { country: ["us", "ca"] },
          ...(autocompleteOptions || {}),
        };
        const ac = new window.google.maps.places.Autocomplete(
          inputRef.current,
          mergedOptions
        );
        acRef.current = ac;
        try {
          // eslint-disable-next-line no-console
          console.log(
            "[Places] Autocomplete attached to grid edit cell",
            mergedOptions
          );
        } catch (_) {}
        ac.addListener("place_changed", () => {
          const place = ac.getPlace();
          try {
            // eslint-disable-next-line no-console
            console.log("[Places][EditCell] place_changed", place);
          } catch (_) {}
          const addr = place?.formatted_address || place?.name || "";
          try {
            api.setEditCellValue({ id, field, value: addr });
            // Commit and close editor
            api.stopCellEditMode({ id, field });
          } catch (_) {}
        });
        setStatus("ready");
      } catch (e) {
        setStatus("error");
        setError(e?.message || "Failed to init Google Places");
        console.log("GoogleAutocompleteEditCell init error:", e);
      }
    };
    init();
    return () => {
      mounted = false;
      const ac = acRef.current;
      if (ac && window.google?.maps?.event?.clearInstanceListeners) {
        try {
          window.google.maps.event.clearInstanceListeners(ac);
        } catch (_) {}
      }
    };
  }, [api, id, field, autocompleteOptions]);

  // Fallback initialization if focus occurs before Places attached
  const ensurePlaces = async () => {
    if (acRef.current) return;
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) return;
      await loadGoogleMapsPlaces(apiKey);
      if (!inputRef.current || !window.google?.maps?.places) return;
      const mergedOptions = {
        componentRestrictions: { country: ["us", "ca"] },
        ...(autocompleteOptions || {}),
      };
      const ac = new window.google.maps.places.Autocomplete(
        inputRef.current,
        mergedOptions
      );
      acRef.current = ac;
      try {
        // eslint-disable-next-line no-console
        console.log(
          "[Places] Autocomplete attached on focus to grid edit cell",
          mergedOptions
        );
      } catch (_) {}
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        try {
          // eslint-disable-next-line no-console
          console.log("[Places][EditCell] place_changed (focus attach)", place);
        } catch (_) {}
        const addr = place?.formatted_address || place?.name || "";
        try {
          api.setEditCellValue({ id, field, value: addr });
          api.stopCellEditMode({ id, field });
        } catch (_) {}
      });
    } catch (_) {}
  };

  const onKeyDown = (event) => {
    if (event.key === KEY_ESCAPE) {
      event.preventDefault();
      try {
        api.stopCellEditMode({ id, field, ignoreModifications: true });
      } catch (_) {}
      return;
    }
    event.stopPropagation();
  };

  return (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      value={internalValue}
      onChange={handleChange}
      onFocus={ensurePlaces}
      onKeyDown={onKeyDown}
      inputRef={inputRef}
      // Ensure the input receives focus when in edit mode
      autoFocus
      autoComplete="off"
      sx={{
        height: "100%",
        "& .MuiInputBase-root": {
          height: "100%",
          fontSize: 13,
          alignItems: "center",
        },
        "& .MuiOutlinedInput-input": { padding: "4px 8px" },
      }}
    />
  );
}

export default GoogleAutocompleteEditCell;
